import { recommendationsPrebuiltViews } from "@features/recommendations-feature/recommendationsPrebuiltViews";
import {
  AzureRecommendationSummaryDto,
  RecommendationDto,
  ResponseRecommendation,
  RoadmapItemSourceType,
} from "@lib/ShiOneClient";
import shiColors from "shared-ui/src/theme/shiColors";
import {
  awsCloudSourceSystems,
  AwsRecommendationSummary,
  cloudPlatformSourceSystems,
  recommendationCloudPlatforms,
  recommendationSourceSystems,
} from "@features/recommendations-feature/utils/recommendationTypes";

export function getCloudPlatformTitle(sourceSystem: string): string {
  if (sourceSystem === recommendationSourceSystems.AzureSimpleRecommendations) {
    return recommendationCloudPlatforms.Azure;
  }
  if (awsCloudSourceSystems.includes(sourceSystem)) {
    return recommendationCloudPlatforms.AWS;
  }
  return "";
}

export const recImpactColor: { [key: string]: string } = {
  "high impact": shiColors.red.DEFAULT,
  high: shiColors.red.DEFAULT,
  "medium impact": shiColors.orange.lighter,
  medium: shiColors.orange.lighter,
  "low impact": shiColors.green.DEFAULT,
  low: shiColors.green.DEFAULT,
};

export function getSourceId(rec: any): string {
  if (rec.sourceSystem === recommendationSourceSystems.Assessments) {
    const parsedMetadata = parseMetadata(rec.metadata);
    return parsedMetadata?.Data?.sourceId ?? "";
  }

  return rec.recommendationId || "";
}

export const getTotalMonthlySavingsAmount = (rec: any): number | null => {
  const savings = rec?.parsedMetadata?.totalMonthlySavings;
  return typeof savings === "number" && savings > 0 ? savings : null;
};

export function formatCategoryLabel(category: string): string {
  if (category === "OperationalExcellence") return "Operational Excellence";
  if (category === "HighAvailability") return "High Availability";
  return category;
}

export const parseResourceFromArn = (arn: string) => {
  const arnParts = arn.split(":");
  if (arnParts.length >= 6) {
    const resourcePart = arnParts.slice(5).join(":");
    const [resourceType, ...resourceIdParts] = resourcePart.split("/");
    return {
      resourceType: resourceType || "Instance",
      resourceId: resourceIdParts.join("/") || arn,
    };
  }
  return { resourceType: "Resource", resourceId: arn };
};

export const getSecurityAffectedResources = (parsedMetadata: any) => {
  const securityMetadata = parsedMetadata?.securitySpecificMetadata;
  if (!securityMetadata?.resourceIds) return [];

  return securityMetadata.resourceIds.map((arn: string) => {
    const { resourceType, resourceId } = parseResourceFromArn(arn);
    return {
      resourceType: securityMetadata.resourceTypes?.[0] || resourceType,
      resourceName: resourceId,
      resourceId: arn,
    };
  });
};

export const getWorkflowStatusConfig = (status: string) => {
  const configs: Record<
    string,
    { borderColor: string; color: string; label: string }
  > = {
    NEW: {
      borderColor: "warning.main",
      color: "warning.main",
      label: "New",
    },
    NOTIFIED: {
      borderColor: "info.main",
      color: "info.main",
      label: "Notified",
    },
    SUPPRESSED: {
      borderColor: "text.disabled",
      color: "text.primary",
      label: "Suppressed",
    },
    RESOLVED: {
      borderColor: "success.main",
      color: "success.main",
      label: "Resolved",
    },
  };

  return configs[status] || { color: "text.secondary", label: status };
};

export function getParsedFields(parsedMetadata: any, sourceSystem?: string) {
  const RecommendationMetadata = parsedMetadata?.RecommendationMetadata || {};
  const ResponseRecommendation = parsedMetadata?.ResponseRecommendation || {};
  const Assessment = parsedMetadata?.Assessment || {};

  if (sourceSystem && cloudPlatformSourceSystems.includes(sourceSystem)) {
    const impactRaw = parsedMetadata?.impact || parsedMetadata?.riskLevel || "";
    let impact: string | undefined;
    let cloudType: string | undefined;
    let category: string | undefined;

    switch (impactRaw) {
      case "High":
        impact = "High Impact";
        break;
      case "Medium":
        impact = "Medium Impact";
        break;
      case "Low":
        impact = "Low Impact";
        break;
      default:
        impact = impactRaw;
    }

    switch (sourceSystem) {
      case recommendationSourceSystems.AzureSimpleRecommendations:
        cloudType = recommendationCloudPlatforms.Azure;
        category = parsedMetadata?.category;
        break;
      case recommendationSourceSystems.AwsSavingsPlansPurchase:
      case recommendationSourceSystems.AwsReservationPurchase:
      case recommendationSourceSystems.AwsRightsizing:
        cloudType = recommendationCloudPlatforms.AWS;
        category = "Cost";
        break;
      case recommendationSourceSystems.AwsSecurityCompliance:
        cloudType = recommendationCloudPlatforms.AWS;
        category = "Security";
        break;
      default:
        cloudType = undefined;
    }

    return {
      impact,
      category: category,
      program: cloudType,
    };
  }

  return {
    impact:
      RecommendationMetadata.impact ||
      ResponseRecommendation.Impact ||
      parsedMetadata?.Data?.impact,
    category:
      RecommendationMetadata.category ||
      ResponseRecommendation.Category ||
      parsedMetadata?.Data?.category,
    program: Assessment.Program || ResponseRecommendation.Program,
  };
}

export function getTabFilters(tabIndex: number) {
  switch (tabIndex) {
    case 1:
      return (
        recommendationsPrebuiltViews.find(
          (p) => p.name === recommendationSourceSystems.Assessments,
        )?.filters ?? []
      );
    case 2:
      return (
        recommendationsPrebuiltViews.find((p) => p.name === "Cloud")?.filters ??
        []
      );
    case 0:
    default:
      return [];
  }
}

export const parseMetadata = (metadata: string | null | undefined): any => {
  if (!metadata) return null;
  try {
    return JSON.parse(metadata);
  } catch {
    return null;
  }
};

/**
 * Build source ID map and normalized recommendations from a list of recommendations
 */
export const processRecommendations = (
  recommendations: any[],
): {
  sourceIdMap: Map<string, string>;
  normalizedRecommendations: any[];
} => {
  const sourceIdMap = new Map<string, string>();
  const normalizedRecommendations: any[] = [];

  recommendations.forEach((rec) => {
    const sourceId = getSourceId(rec);
    const capability = rec.title;

    if (sourceId && capability) {
      sourceIdMap.set(sourceId, capability);
    }

    const normalizedRec = buildNormalizedRecommendation(rec);
    if (normalizedRec) {
      normalizedRecommendations.push(normalizedRec);
    }
  });

  return { sourceIdMap, normalizedRecommendations };
};

/**
 * Build normalized recommendation
 */
export const buildNormalizedRecommendation = (
  rec: RecommendationDto,
):
  | ResponseRecommendation
  | AzureRecommendationSummaryDto
  | AwsRecommendationSummary
  | null => {
  const parsedMetadata = parseMetadata(rec.metadata);

  if (rec.sourceSystem === recommendationSourceSystems.Assessments) {
    const assessmentInfo = parsedMetadata?.Assessment;
    const assessmentId =
      assessmentInfo?.Id ||
      parsedMetadata?.RecommendationMetadata?.assessmentId ||
      rec.resourceIdentifier ||
      "";

    return new ResponseRecommendation({
      assessmentId: assessmentId,
      program: parsedMetadata?.Assessment?.Program,
      capability: rec.title,
      recommendationsSummary: rec.description,
      recommendationId: parsedMetadata?.Data?.id,
      isLinkedToRoadmap: rec.isLinkedToRoadmap,
      sourceType: RoadmapItemSourceType.AssessmentRecommendation,
      sourceMetadata: parsedMetadata && parsedMetadata.ResponseRecommendation,
    });
  }
  if (rec.sourceSystem && awsCloudSourceSystems.includes(rec.sourceSystem)) {
    return {
      recommendationId: rec.recommendationId ?? "",
      recommendationTypeId:
        parsedMetadata?.recommendationTypeId || rec.sourceRecordId,
      customerId: rec.customerId,
      category: parsedMetadata?.category || rec.category,
      impact:
        parsedMetadata?.impact || parsedMetadata?.riskLevel || rec.priority,
      problem: parsedMetadata?.problem || rec.title,
      solution: parsedMetadata?.solution || rec.description,
      affectedScopes: parsedMetadata?.affectedScopes,
      totalAffectedScopes: parsedMetadata?.totalAffectedScopes,
      totalAffectedResources: parsedMetadata?.totalAffectedResources,
      isLinkedToRoadmap: rec.isLinkedToRoadmap,
      sourceId: rec.sourceRecordId,
      sourceSystem: rec.sourceSystem,
      sourceType: RoadmapItemSourceType.AwsCloudRecommendation,
      sourceMetadata: parsedMetadata,
    };
  }

  if (
    rec.sourceSystem === recommendationSourceSystems.AzureSimpleRecommendations
  ) {
    return new AzureRecommendationSummaryDto({
      recommendationId: rec.recommendationId,
      recommendationTypeId:
        parsedMetadata?.recommendationTypeId || rec.sourceRecordId,
      problem: parsedMetadata?.problem || rec.title,
      solution: parsedMetadata?.solution || rec.description,
      category: parsedMetadata?.category || rec.category,
      impact: parsedMetadata?.impact || rec.priority,
      totalAffectedSubscriptions:
        parsedMetadata?.totalAffectedSubscriptions || 1,
      totalAffectedResources: parsedMetadata?.totalAffectedResources || 0,
      sourceId: rec.sourceRecordId,
      affectedSubscriptions: parsedMetadata?.affectedSubscriptions,
      isLinkedToRoadmap: rec.isLinkedToRoadmap,
      sourceSystem: rec.sourceSystem,
      sourceType: RoadmapItemSourceType.AzureCloudRecommendation,
      sourceMetadata: parsedMetadata,
    });
  }

  return null;
};

export const getResourceItemKey = (
  resourceId: string | undefined,
  position: number,
) => {
  return `${resourceId ?? "unknown"}-${position}`;
};
